import {
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpRequest,
  HttpHandlerFn
} from '@angular/common/http';
import {
  catchError, throwError, BehaviorSubject,
  Observable, filter, take, switchMap
} from 'rxjs';
import { AlertService } from '../../shared/alert/service/alert.service';
import { Router } from '@angular/router';
import { AuthService } from '../../features/auth/auth.service';
import { inject } from '@angular/core';
import { MaintenanceWindowService } from '../services/api/maintenanceWindow.service';

// ─── Module-level state (shared across all interceptor calls) ────────────────

let isRefreshing = false;
let sessionExpiredShown = false;
let serviceUnavailableShown = false;

// Queues requests that arrive while a refresh is already in progress.
// Emits the new token (or null on failure) so they can retry.
const refreshSubject = new BehaviorSubject<string | null>(null);

// Auth endpoints that must never trigger a refresh loop
const SKIP_REFRESH_URLS = [
  '/auth/login',
  '/auth/gauth',
  '/auth/refresh-token',
  '/auth/send-otp',
  '/auth/register',
  '/auth/validate-qr',
  '/auth/logout',
  '/auth/csrf-token'
];
// ─── Utility (kept in case you need it elsewhere) ────────────────────────────

function getCookie(name: string): string | null {
  const match = document.cookie.match(
    new RegExp('(^| )' + name + '=([^;]+)')
  );
  return match ? decodeURIComponent(match[2]) : null;
}

// ─── csrfInterceptor — unchanged ─────────────────────────────────────────────

export const csrfInterceptor: HttpInterceptorFn = (req, next) => {
  const csrfToken = localStorage.getItem('csrf_token');
  // const csrfToken = getCookie('XSRF-TOKEN');

  if (csrfToken && !['GET', 'HEAD', 'OPTIONS'].includes(req.method)) {
    req = req.clone({
      setHeaders: { 'X-CSRF-Token': csrfToken },
      withCredentials: true
    });
  }

  return next(req);
};

// ─── authInterceptor — attach token + handle 401 with silent refresh ─────────

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const router      = inject(Router);
  const alert       = inject(AlertService);
  const maintenanceService = inject(MaintenanceWindowService);

  // Attach Bearer token to every request
  const token = authService.getToken();
  if (token) {
    req = req.clone({
      setHeaders: { Authorization: `Bearer ${token}` },
      withCredentials: true
    });
  }

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      const isAuthUrl = SKIP_REFRESH_URLS.some(url => req.url.includes(url));

      // Only attempt refresh for 401s that aren't from auth endpoints
      if (error.status === 401 && !isAuthUrl) {
        return handle401(req, next, authService, router, alert);
      }

      if (error.status === 503) {
        const err = error.error;

        if (err?.maintenance) {
          const data = {
            message:          err.message          ?? '',
            title:            err.title            ?? 'System Maintenance',
            retryAfter:       err.retryAfter       ?? 0,
            estimatedEndTime: err.estimatedEndTime ?? err.end_time ?? err.endTime ?? '',
            notify_roles:     err.notify_roles     ?? err.notifyRoles ?? err.roles ?? [], // ✅ fixed
            notes_impact:     err.notes_impact     ?? err.notesImpact ?? err.message ?? '',
            start_time:       err.start_time       ?? err.startTime ?? '',
            end_time:         err.end_time         ?? err.endTime   ?? err.estimatedEndTime ?? '',
          };

          maintenanceService.show(data);
          localStorage.setItem('maintenance_data', JSON.stringify(data));

        } else {
          if (!serviceUnavailableShown) {
            serviceUnavailableShown = true;
            alert.show('Service temporarily unavailable.', 'error', 5000);
            setTimeout(() => serviceUnavailableShown = false, 5000);
          }
        }

        return throwError(() => error);
      }
      return throwError(() => error);
    })
  );
};

// ─── 401 handler ─────────────────────────────────────────────────────────────

function handle401(
  req: HttpRequest<any>,
  next: HttpHandlerFn,
  authService: AuthService,
  router: Router,
  alert: AlertService
): Observable<any> {

  // Another refresh is already running — queue this request
  if (isRefreshing) {
    return refreshSubject.pipe(
      filter(t => t !== null),   // wait until refresh completes
      take(1),
      switchMap(newToken =>
        next(req.clone({
          setHeaders: { Authorization: `Bearer ${newToken}` }
        }))
      )
    );
  }

  // Start a fresh refresh
  isRefreshing = true;
  refreshSubject.next(null);

  return authService.refreshToken().pipe(
    switchMap(res => {
      isRefreshing = false;
      const newToken = res?.data?.accessToken;

      if (!newToken) {
        // Backend responded but gave us nothing usable — treat this the
        // same as a failed refresh instead of queuing retries with a
        // broken/undefined token.
        refreshSubject.next(null);
        return throwError(() => new Error('Refresh did not return an access token'));
      }

      refreshSubject.next(newToken);

      // Retry the original request with the new token
      return next(req.clone({
        setHeaders: { Authorization: `Bearer ${newToken}` }
      }));
    }),
    catchError(refreshError => {
      // Refresh failed — session is truly dead
      isRefreshing = false;
      refreshSubject.next(null);

      if (!sessionExpiredShown) {
        sessionExpiredShown = true;
        authService.removeToken();

        alert.show(
          'Your session has expired. Please login again.',
          'warning',
          0,
          {
            actionText: 'Login',
            cancelText: 'Stay',
            onAction: () => {
              sessionExpiredShown = false;
              router.navigate(['/login']);
            }
          }
        );
      }

      return throwError(() => refreshError);
    })
  );
}

